import { useState } from "react";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import SearchableCouponSelectMenu from "@/app/components/ui/SelectMenus/SearchableCouponSelectMenu";
import { TrashIcon } from "@heroicons/react/24/outline";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useDeleteCartItemAPI } from "@/app/hooks/api_hooks/HDCart/useDeleteCartItemAPI";

interface CartRowProps {
  item: any;
  min: number;
  updateCartItem: (item: any, quantity: number, couponId: string, isSearchClick: boolean) => void;
  customerId?: string;
  petId?: string;
  cartItemsList?: any;
  mappedClinicId?: string;
  mappedClinicName?: string;
  userLogInfo?: string;
  selectedAddress?: any;
  isCouponApplied?: any;
  cartLevelCouponId?: string;
  fetchCartItemsWithCoupon?: (customerId: string, pincode: any, coupon: any) => void;
}

const CartRow = ({
  item,
  min,
  updateCartItem,
  customerId,
  petId,
  cartItemsList,
  mappedClinicId,
  mappedClinicName,
  userLogInfo,
  selectedAddress,
  isCouponApplied,
  cartLevelCouponId,
  fetchCartItemsWithCoupon,
}: CartRowProps) => {
  const [localQty, setLocalQty] = useState(item.quantity);
  const [_isUpdating, setIsUpdating] = useState(false);
  const { execute: logAPI } = useClickLogAPI();
  const deleteCartItem = useDeleteCartItemAPI();

  const handleQtyChange = async (newQty: number) => {
    if (item?.is_disabled) return;
    setIsUpdating(true);
    await updateCartItem(item, newQty, item.coupon_id, false);
    setIsUpdating(false);
  };

  const handleCouponChange = async (couponId: string) => {
    if (item?.is_disabled) return;
    if (item.coupon_id === couponId) return;
    setIsUpdating(true);
    await updateCartItem(item, item.quantity, couponId, false);
    setIsUpdating(false);
  };

  const handleDeleteItem = async (item: any) => {
    if (!customerId || !selectedAddress?.pincode) return;
    const payload = {
      customer_id: customerId,
      pincode: selectedAddress?.pincode,
      vetic_variant_id: item?.vetic_variant_id,
      unique_row_id: item?.unique_row_id,
    };
    const res = await deleteCartItem.execute(payload);
    callLogAPILabDeleted(item?.unique_row_id);
    if (res?.status) {
      fetchCartItemsWithCoupon(customerId, selectedAddress.pincode, isCouponApplied ? cartLevelCouponId : undefined);
    }
  };

  

  const callLogAPIBase = (
    item: string,
    eventType: "CART_QUANTITY_PLUS" | "CART_QUANTITY_MINUS" | "CART_COUPON" | "CART_ITEM_DELETED",
  ) => {
    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "CART",
      client_id: customerId,
      pet_id: petId,
      emr_cart_items: cartItemsList,
      emr_cart_click_item: item,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      cart_id: cartItemsList?.cart_id,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  };

  const callLogAPILab = (item: any) => callLogAPIBase(item, "CART_QUANTITY_PLUS");
  const callLogAPILabMINUS = (item: any) => callLogAPIBase(item, "CART_QUANTITY_MINUS");
  const callLogAPILabDeleted = (item: any) => callLogAPIBase(item, "CART_ITEM_DELETED");

  return (
    <tr className="border-b sm:text-[11px] text-[12px]">
      <td className="p-3">{item?.name}</td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <div className="relative pb-3">
            <InputWithStartAndEndAddOn
              className={`h-6 text-center ${item?.is_disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
              startAddon="-"
              endAddon="+"
              step={1}
              min={min}
              // max={max}
              type="string"
              disabled={item?.is_disabled}
              value={localQty}
              startAddOnCallback={() => {
                if (!item?.is_disabled && localQty > min) {
                  handleQtyChange(item.quantity - 1);
                }
                callLogAPILabMINUS(item.row_id);
              }}
              endAddOnCallback={() => {
                if (!item?.is_disabled) {
                  handleQtyChange(item.quantity + 1);
                }
                callLogAPILab(item.row_id);
              }}
              onChange={(e: any) => {
                if (!item?.is_disabled) {
                  const value = e.target.value;
                  if (value === "" || /^\d*$/.test(value)) {
                    setLocalQty(value);
                  }
                }
              }}
              onBlur={(e: any) => {
                if (!item?.is_disabled) {
                  let value = parseInt(e.target.value) || min;
                  if (value < min) value = min;
                  setLocalQty(value);
                  if (value !== item.quantity) {
                    handleQtyChange(value);
                  }
                }
              }}
              onKeyDown={(e: any) => {
                if (e.key === "Enter" && !item?.is_disabled) {
                  e.preventDefault();
                  let value = parseInt(e.target.value) || min;
                  if (value < min) value = min;
                  setLocalQty(value);
                  if (value !== item.quantity) {
                    handleQtyChange(value);
                  }
                }
              }}
            />
          </div>
        </div>
      </td>
      <td className="p-3">{item?.unit_price}</td>
      <td className="p-3">{item?.gross_price}</td>
      <td className="p-3">
        <SearchableCouponSelectMenu
          errorMessages={[]}
          onSelectItem={(coupon) => handleCouponChange(coupon ? coupon.id : "")}
          selectItems={
            item?.all_applicable_coupons?.map((coupon) => ({
              id: coupon.coupon_id,
              title: coupon.coupon_name,
            })) || []
          }
          defaultSelected={
            item?.all_applicable_coupons
              ?.filter((coupon) => coupon.is_selected)
              .map((coupon) => ({
                id: coupon.coupon_id,
                title: coupon.coupon_name,
              }))[0] || null
          }
          fieldError={undefined}
          register={undefined}
          optionRenderVariant="DEFAULT"
          placeholder="Select Coupon"
          showTooltips={true}
          showDropdownIcon={true}
          withReset={true}
          listBoxClassName="w-full"
          className="text-gray-800 w-10"
          containerClassName="w-full"
          disabled={item?.is_disabled}
        />
        {item?.selected_coupon && <div className="text-[#00AA4C]">{item?.selected_coupon}</div>}
      </td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{item?.net_price}</span>
          {item?.discount_text && (
            <div className="bg-green-50 border border-[#00AA4C] text-[#00AA4C] px-2 rounded-md sm:text-[8px] text-[10px] font-medium whitespace-nowrap">
              {item?.discount_text}
            </div>
          )}
        </div>
      </td>
      <td className="p-3 text-center">
        <button
          disabled={item?.is_disabled}
          onClick={() => handleDeleteItem(item)}
          className={`${item?.is_disabled ? "text-gray-300 cursor-not-allowed" : "text-gray-500 hover:text-red-500"}`}
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
};

export default CartRow;
